import {
  EmptyState,
  EmptyStateContainer,
} from 'module/ui/components/atoms/empty-state';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { Loading } from 'module/ui/components/atoms/loading';
import { Heading03, HelperText01 } from 'module/ui/components/styles';
import { ReactNode } from 'react';
import styled from 'styled-components';
import { getIcon, Sections } from './content-navigator';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-05);
`;
const HeaderRow = styled.div<{ $hasSubHeadingInContent?: boolean }>`
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
  justify-content: space-between;
  position: sticky;
  top: 0;
  background-color: var(--white-100);
  z-index: 100;
  width: calc(100% + 4px);
  margin-left: -2px;
  margin-right: -2px;
  padding: 0 2px;
  &:before {
    position: absolute;
    content: '';
    width: 100%;
    height: var(--spacing-05);
    top: calc(var(--spacing-05) * -1);
    background: var(--white-100);
    z-index: 99;
  }
  &:after {
    position: absolute;
    content: ${({ $hasSubHeadingInContent }) =>
      $hasSubHeadingInContent ? 'none' : "''"};
    width: 100%;
    height: var(--spacing-03);
    bottom: calc(var(--spacing-03) * -1);
    background: var(--white-100);
    z-index: 99;
  }
`;
const Heading = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-01);
`;
const Title = styled.div`
  ${Heading03};
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
`;
const Description = styled.div`
  ${HelperText01};
  color: var(--text-tertiary);
`;
const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 300px;
`;

type Props = {
  icon: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  headerActions?: ReactNode;
  loading?: boolean;
  children: ReactNode;
  sectionKey: string;
  className?: string;
  hasSubHeadingInContent?: boolean;
};
export const ContentSection = ({
  icon,
  title,
  description,
  headerActions,
  loading,
  children,
  sectionKey,
  className,
  hasSubHeadingInContent,
}: Props) => {
  return (
    <Wrapper data-section-key={sectionKey} className={className}>
      <HeaderRow $hasSubHeadingInContent={hasSubHeadingInContent}>
        <Heading>
          <Title>
            {icon}
            {title}
          </Title>
          {description != null ? (
            <Description>{description}</Description>
          ) : null}
        </Heading>
        <div>{headerActions}</div>
      </HeaderRow>
      {loading != null && loading ? (
        <LoadingWrapper>
          <Loading />
        </LoadingWrapper>
      ) : (
        <div>{children}</div>
      )}
    </Wrapper>
  );
};

type EmptyStateForSectionProps = {
  icon?: ReactNode;
  message: ReactNode;
  action?: ReactNode;
  sectionKey: Sections;
};
const EmptyStateWrapper = styled(EmptyStateContainer)`
  background-color: var(--scarlet-gray-10);
`;

export const EmptyStateForSection = ({
  icon,
  message,
  action,
  sectionKey,
}: EmptyStateForSectionProps) => {
  return (
    <EmptyStateWrapper>
      <EmptyState
        image={icon ?? <FontAwesomeIcon icon={getIcon(sectionKey)} size={32} />}
        message={message}
        primaryCTA={action}
      />
    </EmptyStateWrapper>
  );
};
